import { AnchorHTMLAttributes, MouseEvent, ReactNode, Ref, forwardRef } from 'react';

import reactToText from 'react-to-text';

import * as s from './Tag.css';
import { getClassNames } from '../../util/css';
import { RecipeVariants } from '../../vanilla-extract.css';

type TagRecipeVariants = RecipeVariants<typeof s.tagRecipe>;

const sparkyClassName = 'sparky-tag';

export const Tag = forwardRef<HTMLAnchorElement | HTMLButtonElement, TagProps>(
  ({ children, isCurrent = false, href, target, onClick, className = '', ariaControls }, ref) => {
    const tagClass = getClassNames(
      sparkyClassName,
      s.tagRecipe({ isCurrent }),
      className,
    );

    const commonProps = {
      className: tagClass,
      'data-label': reactToText(children),
      onClick,
    };

    return href ? (
      <a
        {...commonProps}
        href={href}
        target={target || undefined}
        rel={target === '_blank' ? 'noreferrer' : undefined}
        aria-current={isCurrent ? 'page' : undefined}
        ref={ref as Ref<HTMLAnchorElement>}>
        {children}
      </a>
    ) : (
      <button
        {...commonProps}
        type="button"
        aria-controls={ariaControls}
        aria-pressed={isCurrent}
        ref={ref as Ref<HTMLButtonElement>}>
        {children}
      </button>
    );
  },
);

Tag.toString = () => `.${sparkyClassName}`;
Tag.displayName = 'Tag';

type TagButtonProps = {
  href?: never;
  target?: never;
  /** The id of the element that contains the filtered content */
  ariaControls: string;
};

type TagAnchorProps = {
  href?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  ariaControls?: never;
};

type TagProps = TagRecipeVariants & {
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  /** Whether or not the Tag has been selected */
  isCurrent?: boolean;
  /** Text shown inside the link. */
  children: ReactNode;
  className?: string;
} & (TagAnchorProps | TagButtonProps);
